import React, { ErrorInfo, PropsWithChildren, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button, Flex, Text, Theme } from "@radix-ui/themes";
import { ReloadIcon } from "@radix-ui/react-icons";

interface ErrorBoundaryProps {
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<
  PropsWithChildren<ErrorBoundaryProps>,
  ErrorBoundaryState
> {
  constructor(props: PropsWithChildren<ErrorBoundaryProps>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Send error details to Sentry
    Sentry.captureException(error, {
      extra: {
        errorInfo,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return this.props.fallback || <DefaultFallback />;
    }
    return this.props.children;
  }
}

function DefaultFallback() {
  return (
    <Theme
      accentColor="gray"
      grayColor="slate"
      panelBackground="solid"
      radius="large"
    >
      <Flex
        width={"100dvw"}
        height={"100dvh"}
        direction="column"
        align="center"
        justify="center"
        gap="4"
      >
        <Text color={"gray"} size={"6"} weight={"medium"} highContrast>
          Something Went Wrong
        </Text>

        <Text color={"gray"} size={"2"}>
          Please refresh the page or contact support if the problem persists.
        </Text>

        <Flex pt="4" className="z-10">
          <Button
            variant="outline"
            color="gray"
            size="3"
            onClick={() => window.location.reload()}
          >
            <ReloadIcon />
            Reload Page
          </Button>
        </Flex>
        <img src="/background.png" className=" absolute bottom-0 z-0" />
      </Flex>
    </Theme>
  );
}

export default ErrorBoundary;
